import { css } from '@emotion/react'
import styled from '@emotion/styled'
import SendIcon from '@mui/icons-material/Send'
import Divider from '@mui/material/Divider'
import {
    ChangeEvent,
    KeyboardEvent,
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { setFullWindow } from '../../../actions/layout'
import { sendTypingIndicator } from '../../../actions/message'
import { openTipsDrawer } from '../../../app-state/modals/state.drawer'
import { EMOJI_ANCHOR_LOCATIONS } from '../../../constants/enums'
import { ParseEmoteInput } from '../../../libraries/mfc/ParseEmoteInput.js'
import { isLounge as isRoomLounge } from '../../../mfcLogic/userProperties/user'
import { idToSlug } from '../../../state/state.users'
import { CommandsStore } from '../../../utilities/commandsStore'
import i18n from '../../../utilities/i18n'
import {
    motionTransition,
    TransitionTypes,
} from '../../../utilities/transitions'
import ButtonChatHide from '../../Buttons/ButtonChatHide'
import ButtonFullWindowEnter from '../../Buttons/ButtonFullWindowEnter'
import ButtonFullWindowExit from '../../Buttons/ButtonFullWindowExit'
import ButtonTip from '../../Buttons/ButtonTip'
import ButtonZoomIn from '../../Buttons/ButtonZoomIn'
import IconButton from '../../Buttons/IconButton'
import { inputStyles } from '../../common/Forms'
import Input from '../../common/Input'
import { AppStateType, useAppState } from '../../Hooks/useAppState'
import { useHealthAppStateSelector } from '../../Hooks/useHealthAppState'
import { useLayoutSelector } from '../../Hooks/useLayout'
import { useRoomSelector } from '../../Hooks/useRoom'
import { useRouteSelector } from '../../Hooks/useRoute'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import { useUserSelector } from '../../Hooks/useUser'
import { getThemeSpacing } from '../../styles/appTheme'
import { palette } from '../../styles/colors'
import shape from '../../styles/shape'
import ChatImagesButton from '../ChatImages/ChatImagesButton'

export const rootStyles = css({
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    width: '100%',
    flexGrow: 0,
    overflow: 'visible',
    position: 'relative',
    '& input': {
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
    },
})
const fullWindowStyles = css({
    '& svg': {
        color: palette.greyMFC,
        fontSize: '1.4rem',
    },
    '& hr': {
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
})
const pinnedPmStyles = css({
    borderBottomLeftRadius: shape.borderRadius,
    borderBottomRightRadius: shape.borderRadius,
})

const composeSendStyles = css({
    border: 0,
    padding: getThemeSpacing(1),
})
const composeGreenSendStyles = css({
    '& svg': {
        color: palette.greenMFC,
    },
})

const StyledDivider = styled(Divider)({
    width: 1,
    height: 28,
    margin: 4,
    backgroundColor: palette.drawerDivider,
    fontSize: '0.8125rem',
    border: 'none',
})
const StyledSendIcon = styled(SendIcon)({
    color: palette.chatSend,
    fontSize: '1.4rem',
})

interface IComposeProps {
    children?: ReactNode
    vertical?: boolean
    disabled?: boolean
    disableKeyboardOpenDetection?: boolean
    hide?: boolean
    hideTipButton?: boolean
    id: number
    noFullWindowButtons?: boolean
    onSend?(id: number, parsed: string): void
    placeholder?: string
    pm?: boolean
    pinnedPm?: boolean
    showZoomVideo?: boolean
    setMinimized?(minimized: boolean): void
    setZoomVideo?(): void
}

export const processMessageWithSymbol = (
    selStart: number,
    message: string,
    symbolTextValue: string,
) => {
    const insertSpaceBefore = message.charAt(selStart - 1).indexOf(' ') === -1
    const insertSpaceAfter = message.charAt(selStart).indexOf(' ') === -1
    return (
        message.slice(0, selStart) +
        (insertSpaceBefore ? ' ' : '') +
        symbolTextValue +
        (insertSpaceAfter ? ' ' : '') +
        message.slice(selStart)
    )
}

const Compose = memo((props: IComposeProps) => {
    const { darkMode, isMobile } = useAppState(AppStateType.APP)
    const level = useUserSelector('me', 'level')
    const socketConnected = useHealthAppStateSelector('connected')
    const fullWindow = useLayoutSelector('fullWindow')
    const portrait = useScreenStateSelector('portrait')

    const commandsStore = useRef<CommandsStore>(null)
    const focused = useRef(false)
    const inputRef = useRef<HTMLInputElement>(undefined)
    const lastChatFlashSent = useRef(0)
    const selectionStart = useRef(0)
    const stagedImages = useRef<Record<string, string>>({})
    const parser = useRef<ParseEmoteInput>(null)
    const [message, setMessage] = useState('')

    const {
        children,
        disabled = !socketConnected,
        disableKeyboardOpenDetection,
        hide,
        hideTipButton,
        id,
        noFullWindowButtons,
        onSend,
        placeholder,
        pm,
        pinnedPm,
        setMinimized,
        showZoomVideo, // if this is present then we can show the icon
        setZoomVideo,
    } = props

    const isPm = pm || pinnedPm

    const loungeSurrogateID = useRoomSelector(id, 'loungeSurrogateID')
    const visiblePage = useRouteSelector('visiblePage')
    const isModel = useUserSelector(id, 'isModel')
    const slug = useUserSelector(id, 'slug')
    const lounge = isRoomLounge(slug)
    const isMessagePage = visiblePage === 'message'
    const isRoomPage = visiblePage === 'room'
    const isLounge = isRoomPage && lounge
    const resolvedSlug = isLounge ? idToSlug[loungeSurrogateID] : slug

    const isTipButtonRelevant = isMessagePage
        ? isModel
        : isLounge
          ? !!loungeSurrogateID
          : isRoomPage
    const showTipButton = (isTipButtonRelevant && !hideTipButton) || pinnedPm

    const canShowFullWindowButtons =
        !noFullWindowButtons && !portrait && isModel
    const showEnterFullWindowButton = canShowFullWindowButtons && !fullWindow
    const showExitFullWindowButton = canShowFullWindowButtons && fullWindow

    const combinedRootStyles = [rootStyles]
    if (fullWindow) {
        combinedRootStyles.push(fullWindowStyles)
    }
    if (pinnedPm) {
        combinedRootStyles.push(pinnedPmStyles)
    }

    useEffect(() => {
        commandsStore.current = new CommandsStore()
        return () => {
            commandsStore.current = null
        }
    }, [])

    useEffect(() => {
        parser.current = new ParseEmoteInput({
            anchor: EMOJI_ANCHOR_LOCATIONS.top,
            darkMode,
        })
        return () => {
            parser.current = null
        }
    }, [darkMode])

    const updateState = useCallback((msg: string) => {
        selectionStart.current = msg.length
        if (!inputRef.current) return
        setMessage(msg)
        inputRef.current.selectionStart = msg.length
        inputRef.current.selectionEnd = msg.length
    }, [])

    const onInsertEmoticon = useCallback(
        (emoticonTextValue: string) => {
            updateState(
                processMessageWithSymbol(
                    focused.current
                        ? inputRef.current?.selectionStart
                        : selectionStart.current,
                    message,
                    emoticonTextValue,
                ),
            )
        },
        [message, updateState],
    )

    const onInsertImage = useCallback(
        (i: { shortcut: string; src: string }) => {
            const { shortcut, src } = i
            stagedImages.current[shortcut] = src.replace(
                /.*\/(\w+)(\.gif|\.jpg|\.jpeg)/,
                '$1$2',
            )
            updateState(
                processMessageWithSymbol(
                    focused.current
                        ? inputRef.current?.selectionStart
                        : selectionStart.current,
                    message,
                    shortcut,
                ),
            )
        },
        [message, updateState],
    )

    const parseMessageWithImages = useCallback((message: string) => {
        return message.replace(
            /\s(::(\w+))/g,
            (_match, p1: string, p2: string) => {
                return `#~u,${stagedImages.current[p1]},${p2}~# `
            },
        )
    }, [])

    const onMessage = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target
        setMessage(value)
    }, [])

    const sendMessage = useCallback(
        (msg: string) => {
            parser.current?.inlineEmotePreview_close()
            commandsStore.current?.resetIndex()
            if (msg.startsWith('/')) {
                commandsStore.current?.put(msg)
            }
            if (
                msg.startsWith('/') &&
                !msg.startsWith('/w ') &&
                !msg.startsWith('/whisper') &&
                !msg.startsWith('/pm')
            ) {
                onSend?.(id, msg)
            } else {
                parser.current?.Process(msg, (parsed: string) => {
                    const nextMessage = parseMessageWithImages(parsed)
                    stagedImages.current = {}
                    onSend?.(id, nextMessage)
                })
            }
            setMessage('')
        },
        [id, onSend],
    )

    const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
        const updateRefsState = (val: string) => {
            if (inputRef.current) {
                inputRef.current.value = val
                inputRef.current.selectionStart = val.length
                inputRef.current.selectionEnd = val.length
            }
            selectionStart.current = val.length
            setMessage(val)
        }
        const { key } = event
        if (key === 'ArrowUp') {
            event.preventDefault()
            const val = commandsStore.current?.prev()
            if (val !== undefined && inputRef.current) {
                updateRefsState(val)
            }
        } else if (key === 'ArrowDown') {
            event.preventDefault()
            const val = commandsStore.current?.next()
            if (val !== undefined) {
                updateRefsState(val)
            }
        } else if (key === 'ArrowLeft' || key === 'ArrowRight') {
            if (inputRef.current.value.length !== 0) {
                event.stopPropagation()
            }
        }
    }, [])

    const onKeyUp = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            const oMessageInput = event.target as HTMLInputElement
            parser.current.oTextInput = oMessageInput
            parser.current.setComposeMessage = setMessage
            if (
                !isMobile &&
                oMessageInput &&
                typeof oMessageInput.value !== 'undefined'
            ) {
                parser.current?.inlineEmotePreview({
                    input: oMessageInput,
                    callback:
                        parser.current?.inlineEmotePreview_renderPreviewImage,
                    access_level: level,
                })
            }
            if (event.key !== 'Enter') {
                selectionStart.current = inputRef.current.selectionStart
                if (!isMessagePage) return
                const now = performance.now()
                if (now - lastChatFlashSent.current < 4 * 1000) return
                sendTypingIndicator(id)
                lastChatFlashSent.current = now
                return
            }
            event.preventDefault()
            sendMessage(message)
        },
        [id, isMessagePage, isMobile, level, message, sendMessage],
    )

    const onClickSend = useCallback(() => {
        sendMessage(message)
    }, [message, sendMessage])

    const onMouseUp = useCallback(() => {
        selectionStart.current = inputRef.current.selectionStart
    }, [])

    const onOpenTip = useCallback(() => {
        openTipsDrawer({
            slug: resolvedSlug,
            hiddenShows: true,
            chatError: true,
        })
        setMessage('')
    }, [message, resolvedSlug])

    const enterFullWindow = useCallback(() => {
        motionTransition(() => setFullWindow(true, true), {
            id,
            transitionSource: TransitionTypes.ROOM,
            transitionDestination: TransitionTypes.ROOM,
        })
    }, [id])

    const exitFullWindow = useCallback(() => {
        motionTransition(() => setFullWindow(false, true), {
            id,
            transitionSource: TransitionTypes.ROOM,
            transitionDestination: TransitionTypes.ROOM,
        })
    }, [id])

    const minimizeChat = useCallback(() => {
        setMinimized(true)
    }, [setMinimized])

    const inputPlaceholder =
        fullWindow && isMobile
            ? 'Send message...'
            : (placeholder ?? 'Type message...')

    return (
        <div
            css={combinedRootStyles}
            style={{
                backgroundColor: fullWindow
                    ? palette.fullWindowComposeBackground
                    : palette.background,
                backgroundImage:
                    fullWindow && !isPm
                        ? palette.fullWindowChatBackground
                        : 'none',
                borderTopRightRadius: fullWindow ? shape.borderRadius : 0,
                // ...safeObj(style),
                ...(hide ? { display: 'none' } : {}),
            }}
        >
            {showTipButton && <ButtonTip onClick={onOpenTip} />}
            <ChatImagesButton
                fullWindow={fullWindow}
                onInsertEmoticon={onInsertEmoticon}
                onInsertImage={onInsertImage}
                disabled={disabled}
            />
            <Input
                css={inputStyles}
                ref={inputRef}
                data-testid="Chat__TextInput"
                placeholder={inputPlaceholder}
                // onBlur={onBlur}
                onChange={onMessage}
                // onFocus={onFocus}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                onMouseUp={onMouseUp}
                value={message}
                disabled={disabled}
                quietFocus={disableKeyboardOpenDetection}
            />
            <IconButton
                css={[
                    composeSendStyles,
                    !fullWindow && !darkMode ? composeGreenSendStyles : null,
                ]}
                style={{
                    paddingRight: fullWindow ? 0 : getThemeSpacing(1),
                }}
                title={i18n.t('chat.send')}
                onClick={onClickSend}
            >
                <StyledSendIcon data-testid="Chat__SubmitButton" />
            </IconButton>
            {!!children && (
                <>
                    <StyledDivider />
                    {children}
                </>
            )}
            {showZoomVideo !== undefined && (
                <>
                    <StyledDivider />
                    {showZoomVideo ? (
                        <ButtonZoomIn onClick={setZoomVideo} />
                    ) : (
                        <ButtonFullWindowExit onClick={setZoomVideo} />
                    )}
                </>
            )}
            {showEnterFullWindowButton && (
                <>
                    <StyledDivider />
                    <ButtonFullWindowEnter
                        onClick={enterFullWindow}
                        style={{
                            marginRight:
                                'calc(env(safe-area-inset-right, 0px) * 0.5)',
                        }}
                    />
                </>
            )}
            {showExitFullWindowButton && (
                <>
                    <StyledDivider />
                    <ButtonFullWindowExit onClick={exitFullWindow} />
                    <ButtonChatHide onClick={minimizeChat} />
                </>
            )}
        </div>
    )
})

export default Compose
